
import {
  HAVEN_SPACE_BUILDING_NAMES,
  havenPopupIdFromBuilding,
  isHavenIsland,
} from "./hotspotPopupContent";
import { isBulletinNote, isPlannerItem } from "./plannerItems";
import {
  applyIslandModels,
  assignDistrictColors,
  cleanLayouts,
  splitOversizedIslands,
} from "./worldLayout";
import { applyBuildingModels } from "./buildingModels";

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function distinctRoomId(userId, index) {
  return `${userId}_room_${index}`;
}

export function distinctSurfaceId(roomId, index) {
  return `surface_${roomId}_${index}`;
}

function isDistinctRoomId(userId, id) {
  if (!id || !userId) return false;
  return new RegExp(`^${escapeRegExp(userId)}_room_\\d+$`).test(id);
}

function isDistinctSurfaceId(roomId, id) {
  if (!id || !roomId) return false;
  return id.startsWith(`surface_${roomId}_`);
}

/** Walk districts tree and invoke fn(room, building) in stable order. */
export function forEachRoom(districts, fn) {
  for (const district of districts || []) {
    for (const island of district.islands || []) {
      for (const building of island.buildings || []) {
        for (const floor of building.floors || []) {
          for (const room of floor.rooms || []) {
            fn(room, building, floor, island, district);
          }
        }
      }
    }
  }
}

function cloneDistricts(districts) {
  return JSON.parse(JSON.stringify(districts || []));
}

function maxExistingRoomIndex(userId, districts) {
  let max = 0;
  forEachRoom(districts, (room) => {
    const match = String(room.id || "").match(
      new RegExp(`^${escapeRegExp(userId)}_room_(\\d+)$`)
    );
    if (match) max = Math.max(max, parseInt(match[1], 10));
  });
  return max;
}

/** Assign room IDs; returns Map(oldId -> newId). */
function assignRoomIds(userId, districts) {
  const idMap = new Map();
  let counter = maxExistingRoomIndex(userId, districts);

  forEachRoom(districts, (room) => {
    const prevId = room.id;
    if (isDistinctRoomId(userId, prevId)) {
      idMap.set(prevId, prevId);
      return;
    }
    counter += 1;
    const nextId = distinctRoomId(userId, counter);
    room.id = nextId;
    if (prevId) idMap.set(prevId, nextId);
  });

  return idMap;
}

function applyBuildingUnlockDefaults(districts) {
  let sawUnlocked = false;

  forEachRoom(districts, (_room, building, _floor, island) => {
    if (isHavenIsland(island)) return;
    if (typeof building.is_unlocked === "boolean") {
      if (building.is_unlocked) sawUnlocked = true;
      return;
    }
  });

  let assignedFirst = sawUnlocked;

  for (const district of districts || []) {
    for (const island of district.islands || []) {
      if (isHavenIsland(island)) continue;
      for (const building of island.buildings || []) {
        if (typeof building.is_unlocked === "boolean") continue;
        if (!assignedFirst) {
          building.is_unlocked = true;
          assignedFirst = true;
        } else {
          building.is_unlocked = false;
        }
      }
    }
  }

  if (!hasUnlockedBuilding(districts)) {
    for (const district of districts || []) {
      for (const island of district.islands || []) {
        if (isHavenIsland(island)) continue;
        const first = island.buildings?.[0];
        if (first) {
          first.is_unlocked = true;
          return;
        }
      }
    }
  }
}

function hasUnlockedBuilding(districts) {
  for (const district of districts || []) {
    for (const island of district.islands || []) {
      if (isHavenIsland(island)) continue;
      for (const building of island.buildings || []) {
        if (building.is_unlocked) return true;
      }
    }
  }
  return false;
}

/** Rooms default locked; first room in first unlocked building unlocks if none set. Skips Haven islands. */
function applyRoomUnlockDefaults(districts) {
  let sawUnlocked = false;

  forEachRoom(districts, (room, _building, _floor, island) => {
    if (isHavenIsland(island)) return;
    if (typeof room.is_unlocked === "boolean" && room.is_unlocked) {
      sawUnlocked = true;
    }
  });

  let assignedFirst = sawUnlocked;

  forEachRoom(districts, (room, building, _floor, island) => {
    if (isHavenIsland(island)) return;
    if (typeof room.is_unlocked === "boolean") return;
    if (!assignedFirst && building.is_unlocked !== false) {
      room.is_unlocked = true;
      assignedFirst = true;
    } else {
      room.is_unlocked = false;
    }
  });
}

/** How many work islands are unlocked by default; the rest gate on progression. */
const UNLOCKED_ISLAND_COUNT = 2;

function listWorkIslands(districts) {
  const islands = [];
  for (const district of districts || []) {
    for (const island of district.islands || []) {
      if (isHavenIsland(island)) continue;
      islands.push(island);
    }
  }
  return islands;
}

/**
 * First UNLOCKED_ISLAND_COUNT work islands: all buildings/rooms unlocked; every
 * other work island: all locked. Haven untouched.
 *
 * Sticky by island id, not tree position: an island already marked
 * is_unlocked stays unlocked even if a later Brain Dump merge inserts or
 * reorders islands ahead of it. Only the remaining unlock slots (if any) are
 * back-filled with the earliest still-locked islands, so islands appended by
 * a later brain dump never bump an island the player already unlocked.
 */
function setWorkIslandLocks(districts = []) {
  const workIslands = listWorkIslands(districts);
  if (!workIslands.length) return;

  const alreadyUnlocked = workIslands.filter((isl) => isl.is_unlocked === true);
  const unlockedIds = new Set(
    alreadyUnlocked.slice(0, UNLOCKED_ISLAND_COUNT).map((isl) => isl.id)
  );

  for (const island of workIslands) {
    if (unlockedIds.size >= UNLOCKED_ISLAND_COUNT) break;
    if (!unlockedIds.has(island.id)) unlockedIds.add(island.id);
  }

  for (const island of workIslands) {
    const isUnlocked = unlockedIds.has(island.id);
    island.is_unlocked = isUnlocked;

    for (const building of island.buildings ?? []) {
      building.is_unlocked = isUnlocked;

      for (const floor of building.floors ?? []) {
        for (const room of floor.rooms ?? []) {
          room.is_unlocked = isUnlocked;
        }
      }
    }
  }
}

/** Building unlock must match room unlock, unlocked building with all locked rooms */
function syncBuildingRoomUnlocks(districts = []) {
  for (const district of districts || []) {
    for (const island of district.islands || []) {
      if (isHavenIsland(island)) continue;
      for (const building of island.buildings || []) {
        const buildingUnlocked = building.is_unlocked !== false;
        for (const floor of building.floors || []) {
          for (const room of floor.rooms || []) {
            room.is_unlocked = buildingUnlocked;
          }
        }
      }
    }
  }
}

export function applyIslandUnlockRules(districts) {
  const next = cloneDistricts(districts);
  setWorkIslandLocks(next);
  syncBuildingRoomUnlocks(next);
  return next;
}

function remapSurfaceRoomIds(surfaceItems, idMap) {
  return (surfaceItems || []).map((item) => {
    const mapped = idMap.get(item.room_id);
    return mapped ? { ...item, room_id: mapped } : { ...item };
  });
}

/** Default room surfaces — must match Unity prefab Hotspot._surfaceId values. */
const DEFAULT_ROOM_SURFACES = [
  "desk",
  "whiteboard",
  "bookshelf",
  "bulletin_board",
  "calendar",
  "couch",
  "painting",
];

/** Add placeholder surface_items so Unity binds every in-room hotspot. */
function ensureDefaultRoomHotspots(districts, surfaceItems) {
  const items = [...(surfaceItems || [])];
  const existing = new Set(
    items
      .filter((i) => i.room_id && i.surface && !isPlannerItem(i))
      .map((i) => `${i.room_id}:${i.surface}`)
  );

  forEachRoom(districts, (room) => {
    if (!room?.id) return;
    for (const surface of DEFAULT_ROOM_SURFACES) {
      const key = `${room.id}:${surface}`;
      if (existing.has(key)) continue;
      items.push({
        room_id: room.id,
        surface,
        title: room.name ? `${room.name}: ${surface}` : surface,
        content: "",
      });
      existing.add(key);
    }
  });

  return items;
}


/** Unity aerial building hotspot sends the literal id "building", not world_json id. */
export const UNITY_PLACEHOLDER_BUILDING_ID = "building";

export function isUnityPlaceholderBuildingId(id) {
  return !id || id === UNITY_PLACEHOLDER_BUILDING_ID;
}

function lookupBuildingById(districts, id) {
  if (!id) return null;
  for (const district of districts || []) {
    for (const island of district.islands || []) {
      const building = (island.buildings || []).find((b) => b.id === id);
      if (building) return { building, island, district };
    }
  }
  return null;
}

function listAllBuildings(districts) {
  const all = [];
  for (const district of districts || []) {
    for (const island of district.islands || []) {
      for (const building of island.buildings || []) {
        all.push({ building, island, district });
      }
    }
  }
  return all;
}

/**
 * Resolve a building record from Unity's buildingID (may be a placeholder).
 * @param {object} hints
 * @param {string} [hints.roomId]
 * @param {string} [hints.locationBuildingId] — only used when preferLocationHint is true
 * @param {boolean} [hints.preferLocationHint] — late re-resolve after world_json loads
 * @param {string} [hints.excludeBuildingId] — skip previous building when Unity sends placeholder id
 */
export function findBuildingRecord(districts, buildingId, hints = {}) {
  const { roomId, locationBuildingId, preferLocationHint, excludeBuildingId, preferHavenIsland } = hints;

  let found = lookupBuildingById(districts, buildingId);
  if (found) return found;

  if (roomId) {
    found = lookupBuildingById(districts, locationFromRoomId(districts, roomId).buildingId);
    if (found) return found;
  }

  if (!isUnityPlaceholderBuildingId(buildingId)) {
    return null;
  }

  if (preferLocationHint && locationBuildingId) {
    found = lookupBuildingById(districts, locationBuildingId);
    if (found) return found;
  }

  const all = listAllBuildings(districts);
  if (!all.length) return null;
  const unlocked = all.filter((r) => r.building.is_unlocked !== false);
  let pool = unlocked.length ? unlocked : all;

  if (excludeBuildingId && pool.length > 1) {
    const other = pool.filter((r) => r.building.id !== excludeBuildingId);
    if (other.length) pool = other;
  }

  if (preferHavenIsland) {
    const havenPool = pool.filter((r) => isHavenIsland(r.island));
    if (havenPool.length) pool = havenPool;
  }

  return pool[0] ?? null;
}

/** Resolve building for Unity inspection UI (placeholder id + location hints). */
export function resolveInspectionBuilding(districts, inspection, location = {}) {
  if (!inspection?.buildingId) return null;
  if (!isUnityPlaceholderBuildingId(inspection.buildingId)) {
    return lookupBuildingById(districts, inspection.buildingId);
  }
  return findBuildingRecord(districts, inspection.buildingId, {
    locationBuildingId: location.buildingId,
    roomId: location.roomId,
    preferLocationHint: true,
  });
}

/** Resolve full currentLocation fields from a building id (Unity or world_json). */
export function locationFromBuildingId(districts, buildingId, hints = {}) {
  const empty = {
    districtId: null,
    islandId: null,
    buildingId: null,
    floorId: null,
    roomId: null,
  };
  if (!buildingId && !hints.roomId && !hints.locationBuildingId) return empty;

  const record = findBuildingRecord(districts, buildingId, hints);
  if (record) {
    const floor = record.building.floors?.[0];
    return {
      districtId: record.district.id,
      islandId: record.island.id,
      buildingId: record.building.id,
      floorId: floor?.id ?? null,
      roomId: null,
    };
  }

  return {
    ...empty,
    buildingId: isUnityPlaceholderBuildingId(buildingId) ? null : buildingId,
  };
}

/** Last room from user_profile.previousSession / profile load. */
export function lastRoomIdFromSession(session) {
  if (!session) return null;
  return session.last_activity?.room_id || session.last_room_id || null;
}

/** Room display name from a resolved location. */
export function roomNameFromLocation(districts, location) {
  if (!location?.roomId) return null;
  for (const district of districts || []) {
    for (const island of district.islands || []) {
      for (const building of island.buildings || []) {
        for (const floor of building.floors || []) {
          const room = (floor.rooms || []).find((r) => r.id === location.roomId);
          if (room?.name) return room.name;
        }
      }
    }
  }
  return null;
}

/** Room + building + island names for a room id — the world-level card's wayfinding path. */
export function pathNamesFromRoomId(districts, roomId) {
  if (!roomId) return null;
  for (const district of districts || []) {
    for (const island of district.islands || []) {
      for (const building of island.buildings || []) {
        for (const floor of building.floors || []) {
          if ((floor.rooms || []).some((r) => r.id === roomId)) {
            return {
              roomName: (floor.rooms || []).find((r) => r.id === roomId)?.name || null,
              buildingName: building.name || null,
              islandName: island.name || null,
            };
          }
        }
      }
    }
  }
  return null;
}

/** Zero-based floor index for GoToFloor (proxy build). */
export function floorIndexInBuilding(districts, buildingId, floorId) {
  const record = findBuildingRecord(districts, buildingId, { locationBuildingId: buildingId });
  if (!record?.building?.floors?.length || !floorId) return 0;
  const idx = record.building.floors.findIndex((f) => f.id === floorId);
  return idx >= 0 ? idx : 0;
}

function createMinimalHavenBuilding(name, { unlocked = true, popupId = null } = {}) {
  const key = popupId || name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  const roomLabel = name === "Haven" ? "Lounge" : name.replace(/^The /, "");
  return {
    id: `b-haven-${key}`,
    name,
    is_unlocked: unlocked,
    floors: [
      {
        id: `f-haven-${key}-1`,
        name: "Ground",
        rooms: [{ id: `r-haven-${key}-1`, name: roomLabel }],
      },
    ],
  };
}

function havenBuildingAlreadyPresent(buildings, popupId, buildingName) {
  return (buildings || []).some((b) => {
    if (havenPopupIdFromBuilding(b) === popupId) return true;
    return String(b.name || "").toLowerCase() === String(buildingName || "").toLowerCase();
  });
}

/**
 * Every world gets a Haven island with space buildings (Lighthouse, Grove, …)
 * so Unity clicks + popups work for new and existing users.
 */
export function ensureHavenIslandInWorld(districts) {
  const list = cloneDistricts(districts);

  let personalDistrict = list.find((d) => {
    const name = String(d.name || "").toLowerCase();
    return name.includes("personal") || (d.islands || []).some((i) => isHavenIsland(i));
  });
  if (!personalDistrict) {
    personalDistrict = { id: "d-personal", name: "Personal", islands: [] };
    list.push(personalDistrict);
  }

  let havenIsland = (personalDistrict.islands || []).find((i) => isHavenIsland(i));
  if (!havenIsland) {
    havenIsland = { id: "i-haven", name: "Haven", buildings: [] };
    personalDistrict.islands = [...(personalDistrict.islands || []), havenIsland];
  }

  havenIsland.buildings = [...(havenIsland.buildings || [])];

  let needsFirstUnlock = !havenIsland.buildings.some((b) => b.is_unlocked !== false);

  for (const [popupId, buildingName] of Object.entries(HAVEN_SPACE_BUILDING_NAMES)) {
    if (havenBuildingAlreadyPresent(havenIsland.buildings, popupId, buildingName)) continue;
    const canUnlock = popupId === "lighthouse" || popupId === "grove" || popupId === "gray_havens";
    const unlocked = needsFirstUnlock && canUnlock;
    havenIsland.buildings.push(
      createMinimalHavenBuilding(buildingName, { unlocked, popupId })
    );
    if (unlocked) needsFirstUnlock = false;
  }

  if (!havenIsland.buildings.some((b) => String(b.name || "").toLowerCase() === "haven")) {
    havenIsland.buildings.push(
      createMinimalHavenBuilding("Haven", {
        unlocked: needsFirstUnlock || !havenIsland.buildings.some((b) => b.is_unlocked),
      })
    );
  }

  return list;
}

/** Remove Haven islands (and empty districts) — for Unity SyncWorldState when Haven is baked in-scene. */
export function stripHavenFromDistricts(districts) {
  const stripped = [];
  for (const district of districts || []) {
    const islands = (district.islands || []).filter((island) => !isHavenIsland(island));
    if (!islands.length) continue;
    stripped.push({ ...district, islands });
  }
  return stripped;
}

function roomIdsInDistricts(districts) {
  const ids = new Set();
  forEachRoom(districts, (room) => {
    if (room?.id) ids.add(room.id);
  });
  return ids;
}

/** Work-only world payload for Unity (drops Personal/Haven district + Haven surface_items). */
export function stripHavenFromWorldPayload(payload) {
  if (!payload) {
    return { id: "world-1", name: "My World", districts: [], surface_items: [] };
  }
  const districts = stripHavenFromDistricts(payload.districts);
  const roomIds = roomIdsInDistricts(districts);
  const surface_items = (payload.surface_items || []).filter(
    (item) => item?.room_id && roomIds.has(item.room_id) && !isPlannerItem(item)
  );
  return {
    id: payload.id || "world-1",
    name: payload.name || "My World",
    districts,
    surface_items,
  };
}

/** True when the world only has Haven islands (no Brain Dump work geography yet). */
export function isStarterHavenOnlyWorld(districts) {
  if (!districts?.length) return true;
  for (const district of districts) {
    for (const island of district.islands || []) {
      if (!isHavenIsland(island)) return false;
    }
  }
  return true;
}

/** In-memory starter world before Brain Dump (work islands only — Haven is in Unity). */
export function buildStarterHavenWorld(userId) {
  return normalizeWorldForSupabase(userId, {
    id: "world-1",
    name: "My World",
    districts: [],
    surface_items: [],
  });
}

export function locationFromRoomId(districts, roomId) {
  const empty = {
    districtId: null,
    islandId: null,
    buildingId: null,
    floorId: null,
    roomId: null,
  };
  if (!roomId) return empty;

  for (const district of districts || []) {
    for (const island of district.islands || []) {
      for (const building of island.buildings || []) {
        for (const floor of building.floors || []) {
          const room = (floor.rooms || []).find((r) => r.id === roomId);
          if (room) {
            return {
              districtId: district.id,
              islandId: island.id,
              buildingId: building.id,
              floorId: floor.id,
              roomId: room.id,
            };
          }
        }
      }
    }
  }

  return { ...empty, roomId };
}

/** Assign surface_<roomId>_N ids per room. */
function assignSurfaceItemIds(surfaceItems) {
  const perRoom = {};
  return (surfaceItems || []).map((item) => {
    const roomId = item.room_id;
    if (!roomId || isPlannerItem(item)) return { ...item };

    if (isDistinctSurfaceId(roomId, item.id)) {
      const suffix = item.id.slice(`surface_${roomId}_`.length);
      const n = parseInt(suffix, 10);
      if (!Number.isNaN(n)) {
        perRoom[roomId] = Math.max(perRoom[roomId] || 0, n);
      }
      return { ...item };
    }

    // Item has a surface id from a prior normalization with a different room id.
    // Preserve it — re-assigning breaks UI dedup that relies on id stability.
    if (/^surface_.+_\d+$/.test(item.id)) {
      const n = parseInt(item.id.slice(item.id.lastIndexOf("_") + 1), 10);
      if (!Number.isNaN(n)) perRoom[roomId] = Math.max(perRoom[roomId] || 0, n);
      return { ...item };
    }

    perRoom[roomId] = (perRoom[roomId] || 0) + 1;
    return { ...item, id: distinctSurfaceId(roomId, perRoom[roomId]) };
  });
}

/**
 * Full normalization for worlds.world_json (idempotent for already-saved worlds).
 */
export function normalizeWorldForSupabase(userId, payload) {
  if (!userId || !payload) {
    return payload;
  }

  const { surface_items: rawSurfaceItems = [], ...rest } = payload;
  const rawDistricts = Array.isArray(rest.districts) ? rest.districts : [];
  const rawSurfaceItemsSafe = Array.isArray(rawSurfaceItems) ? rawSurfaceItems : [];
  // Issue 1: Haven is baked into Unity — keep work geography only in world_json / Supabase.
  const districts = stripHavenFromDistricts(cloneDistricts(rawDistricts));
  // const districts = ensureHavenIslandInWorld(cloneDistricts(rest.districts));
  splitOversizedIslands(districts);
  applyIslandModels(districts);
  applyBuildingModels(districts);
  assignDistrictColors(districts);
  cleanLayouts(districts);

  const roomIdMap = assignRoomIds(userId, districts);
  applyBuildingUnlockDefaults(districts);
  applyRoomUnlockDefaults(districts);
  setWorkIslandLocks(districts);
  syncBuildingRoomUnlocks(districts);

  const validRoomIds = roomIdsInDistricts(districts);
  const remappedSurfaces = remapSurfaceRoomIds(rawSurfaceItemsSafe, roomIdMap).filter((item) => {
    if (!item?.room_id) return true;
    return validRoomIds.has(item.room_id);
  });
  const withHotspots = ensureDefaultRoomHotspots(districts, remappedSurfaces);
  const surface_items = assignSurfaceItemIds(withHotspots);

  return {
    id: rest.id || "world-1",
    name: rest.name || "My World",
    districts: withRoomBookshelves(districts),
    surface_items,
  };
}

/** Split persisted blob into hierarchy + surface_items for React state. */
export function splitWorldPayload(worldJson) {
  if (!worldJson || typeof worldJson !== "object") {
    return {
      hierarchy: { id: "world-1", name: "My World", districts: [] },
      surface_items: [],
    };
  }

  const { surface_items = [], id, name, districts, ...extra } = worldJson;
  void extra;

  return {
    hierarchy: {
      id: id || "world-1",
      name: name || "My World",
      districts: districts || [],
    },
    surface_items: Array.isArray(surface_items) ? surface_items : [],
  };
}

/** Group surface_items by room_id (read-only lookup; items stay in world_json). */
export function surfaceItemsByRoom(surfaceItems) {
  const byRoom = {};
  for (const item of surfaceItems || []) {
    if (!item.room_id) continue;
    if (!byRoom[item.room_id]) byRoom[item.room_id] = [];
    byRoom[item.room_id].push(item);
  }
  return byRoom;
}

/** Unity navigation hotspots — never open desk in React. */
export const NAVIGATION_HOTSPOT_SURFACES = new Set([
  "door",
  "building",
  "exit",
  "hallway",
]);

/** Surfaces whose HotspotClicked should open the desk panel. */
export function isDeskHotspotSurface(surface) {
  const s = String(surface || "").toLowerCase();
  return s === "table" || s === "desk";
}

/** True when Unity ref is a door/building/etc. click (not table content). */
export function isNavigationHotspotRef(hotspotRef) {
  if (hotspotRef == null) return false;
  const ref = String(hotspotRef).trim().toLowerCase();
  if (!ref) return false;
  if (ref === "door" || ref === "building") return true;

  const colonIdx = ref.indexOf(":");
  if (colonIdx > 0) {
    const surface = ref.slice(colonIdx + 1);
    if (NAVIGATION_HOTSPOT_SURFACES.has(surface)) return true;
  }

  return false;
}

/**
 * Resolve a Unity HotspotClicked ref to a surface_item.
 * Supports ids (surface_{roomId}_N) and {room_id}:table format.
 */
export function resolveSurfaceItemFromHotspot(hotspotRef, surfaceItems) {
  if (!hotspotRef || !Array.isArray(surfaceItems) || !surfaceItems.length) return null;

  const ref = String(hotspotRef).trim();
  if (isNavigationHotspotRef(ref)) return null;

  const exact = surfaceItems.find((item) => item.id === ref);
  if (exact) return exact;

  const colonIdx = ref.indexOf(":");
  if (colonIdx > 0) {
    const roomId = ref.slice(0, colonIdx);
    const surface = ref.slice(colonIdx + 1).toLowerCase();
    if (NAVIGATION_HOTSPOT_SURFACES.has(surface)) return null;

    const inRoom = surfaceItems.filter((item) => item.room_id === roomId);
    if (surface) {
      const bySurface = inRoom.find((item) =>
        isDeskHotspotSurface(surface)
          ? isDeskHotspotSurface(item.surface)
          : String(item.surface || "").toLowerCase() === surface
      );
      if (bySurface) return bySurface;
      return null;
    }
    return inRoom.find((item) => isDeskHotspotSurface(item.surface)) || null;
  }

  const inRoom = surfaceItems.filter((item) => item.room_id === ref);
  return inRoom.find((item) => isDeskHotspotSurface(item.surface)) || null;
}

/** Build desk UI documents for one room from persisted surface_items + in-session edits. */
export function deskDocumentsForRoom(roomId, surfaceItems, sessionDeskDocuments = []) {
  if (!roomId) return [];
  const roomItems = (surfaceItems || []).filter(
    (item) => item.room_id === roomId && isDeskHotspotSurface(item.surface) && (item.content || "").trim().length >= 20
  );
  // Saving renames a new item's id; the saved item keeps the old one in sourceSurfaceItemId.
  const isSameDoc = (item, d) =>
    item.id === d.id || item.id === d.sourceSurfaceItemId || (!!item.sourceSurfaceItemId && item.sourceSurfaceItemId === d.id);
  const sessionForRoom = (sessionDeskDocuments || []).filter((d) => roomItems.some((item) => isSameDoc(item, d)));
  const merged = mergeDeskEditsIntoSurfaceItems(roomItems, sessionForRoom);
  return merged.map((item) => {
    const session = sessionForRoom.find((d) => isSameDoc(item, d));
    return {
      id: item.id,
      title: item.title || String(item.content || "").substring(0, 40) || "Table",
      content: item.content || "",
      version: session?.version || 1,
      timestamp: session?.timestamp || Date.now(),
      sourceSurfaceItemId: item.id,
      roomId: item.room_id,
      user_edited: item.user_edited === true || session?.user_edited === true,
    };
  });
}

/** Merge in-memory desk edits back into surface_items before save (matched by surface_item id). */
export function mergeDeskEditsIntoSurfaceItems(surfaceItems, deskDocuments) {
  if (!Array.isArray(surfaceItems) || !surfaceItems.length) return surfaceItems || [];
  if (!Array.isArray(deskDocuments) || !deskDocuments.length) return surfaceItems;

  return surfaceItems.map((item) => {
    const desk = deskDocuments.find(
      (d) => d.id === item.id || d.sourceSurfaceItemId === item.id || d.id === item.sourceSurfaceItemId
    );
    if (!desk) return item;
    return {
      ...item,
      title: desk.title || item.title,
      content: desk.content ?? item.content,
      // Carry the "user actually edited this" stamp into the persisted surface item.
      ...(desk.user_edited || item.user_edited ? { user_edited: true } : {}),
    };
  });
}

/** Merge desk edits and append session-only documents before Supabase save. */
export function surfaceItemsWithDeskEdits(surfaceItems, deskDocuments) {
  const merged = mergeDeskEditsIntoSurfaceItems(surfaceItems || [], deskDocuments);
  if (!Array.isArray(deskDocuments) || !deskDocuments.length) return merged || [];

  // Build known from both id AND sourceSurfaceItemId so that deskDocuments
  // with a stale pre-normalization id are still recognized as already present.
  const known = new Set();
  for (const item of merged || []) {
    known.add(item.id);
    if (item.sourceSurfaceItemId) known.add(item.sourceSurfaceItemId);
  }
  const extras = [];
  for (const desk of deskDocuments) {
    const linkId = desk.sourceSurfaceItemId || desk.id;
    if (known.has(desk.id) || known.has(linkId)) continue;
    if (!desk.roomId) continue;
    extras.push({
      id: desk.id,
      room_id: desk.roomId,
      surface: "desk",
      title: desk.title || "Untitled",
      content: desk.content || "",
    });
    known.add(desk.id);
  }
  return [...(merged || []), ...extras];
}

function bookshelfFromDocuments(roomId, bookshelfDocs) {
  const docs = bookshelfDocs.filter(
    (d) => d && d.status !== "archived" && (!d.roomId || d.roomId === roomId)
  );
  if (!docs.length) return null;

  const books = docs.map((doc) => {
    const book = {
      id: doc.id,
      title: doc.title || "Untitled",
      type: unityBookType(doc.type),
      is_master: Boolean(doc.isMaster),
    };
    return Number.isInteger(doc.slot) ? { ...book, slot: doc.slot } : book;
  });

  if (process.env.NODE_ENV === "development") {
    console.info(`[React → Unity] bookshelf ${roomId}`, books);
  }
  return { id: `shelf-${roomId}`, persist: false, books };
}

function unityBookType(type) {
  switch (type) {
    case "note": return "note";
    case "reference": return "reference";
    case "upload": return "upload";
    case "whiteboard-cards":
    case "whiteboard-canvas": return "whiteboard";
    default: return "document";
  }
}


export function withRoomBookshelves(districts, bookshelfDocs) {
  const useDocs = Array.isArray(bookshelfDocs);
  const next = cloneDistricts(districts);
  forEachRoom(next, (room) => {
    if (!room?.id) return;
    const bookshelf = useDocs ? bookshelfFromDocuments(room.id, bookshelfDocs) : null;
    if (bookshelf) {
      room.bookshelf = bookshelf;
    } else {
      delete room.bookshelf;
    }
  });
  return next;
}

export function buildWorldPayload(hierarchy, surfaceItems, bookshelfDocs) {
  const surface_items = surfaceItems || [];
  return {
    id: hierarchy?.id || "world-1",
    name: hierarchy?.name || "My World",
    districts: withRoomBookshelves(hierarchy?.districts || [], bookshelfDocs),
    surface_items,
  };
}

export function withWhiteboardImage(surfaceItems, roomId, image) {
  const items = Array.isArray(surfaceItems) ? surfaceItems : [];
  if (!roomId) return items;
  let found = false;
  const next = items.map((item) => {
    if (item?.room_id !== roomId || item?.surface !== "whiteboard") return item;
    found = true;
    const { image_path, image_updated_at, ...rest } = item;
    return image ? { ...rest, image_path: image.path, image_updated_at: image.updatedAt } : rest;
  });
  if (!found && image) {
    next.push({
      room_id: roomId, surface: "whiteboard", title: "whiteboard", content: "",
      image_path: image.path, image_updated_at: image.updatedAt,
    });
  }
  return next;
}

/** The notes image lives on the room's bulletin_board hotspot item, never on a note. */
export function withBulletinImage(surfaceItems, roomId, image) {
  const items = Array.isArray(surfaceItems) ? surfaceItems : [];
  if (!roomId) return items;
  const isBoard = (item) =>
    item?.room_id === roomId && item?.surface === "bulletin_board" && !isPlannerItem(item) && !isBulletinNote(item);
  let found = false;
  const next = items.map((item) => {
    if (!isBoard(item)) return item;
    found = true;
    const { image_path, image_updated_at, notes_signature, ...rest } = item;
    return image
      ? { ...rest, image_path: image.path, image_updated_at: image.updatedAt, notes_signature: image.signature }
      : rest;
  });
  if (!found && image) {
    next.push({
      room_id: roomId, surface: "bulletin_board", title: "bulletin_board", content: "",
      image_path: image.path, image_updated_at: image.updatedAt, notes_signature: image.signature,
    });
  }
  return next;
}
